import { FC, useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { AnalyticsByCustomerResponse } from './types';
import ChartErrorState from './ChartErrorState';
import { formatCompact } from '../../utils/localeFormat';
import { seriesPrimary, seriesSecondary, chartChrome } from '../../utils/chartColors';
import './EmissionsByCustomerChart.scss';

interface EmissionsByCustomerChartProps {
  data: AnalyticsByCustomerResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const EmissionsByCustomerChart: FC<EmissionsByCustomerChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  // optional: formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.data.length === 0) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const customers = data.data.map((d) =>
      d.clientId.length > 12 ? `${d.clientId.substring(0, 12)}...` : d.clientId
    );
    const currentValues = data.data.map((d) => d.currentValue);
    const chrome = chartChrome();

    const series: echarts.BarSeriesOption[] = [
      {
        type: 'bar',
        name: String(t('cpi.analytics.currentYear')),
        data: currentValues,
        itemStyle: { color: seriesPrimary() },
        barMaxWidth: 40,
      },
    ];

    if (data.compareWithPreviousYear) {
      const previousValues = data.data.map((d) => d.previousValue ?? 0);
      series.push({
        type: 'bar',
        name: String(t('cpi.analytics.previousYear')),
        data: previousValues,
        itemStyle: { color: seriesSecondary() },
        barMaxWidth: 40,
      });
    }

    const option: echarts.EChartsOption = {
      tooltip: { trigger: 'axis' },
      legend: data.compareWithPreviousYear
        ? { bottom: 0, textStyle: { fontSize: 12 } }
        : undefined,
      grid: {
        left: '3%',
        right: '4%',
        top: '10%',
        bottom: data.compareWithPreviousYear ? '15%' : '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: customers,
        axisLabel: { rotate: 30, fontSize: 11, color: chrome.muted },
      },
      yAxis: {
        type: 'value',
        name: String(t('cpi.axis.emissionsKg')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        axisLabel: { formatter: (v: number) => formatCompact(v, lng), color: chrome.muted },
      },
      series,
    };

    chartInstance.current.setOption(option, true);
  }, [data, chartNode, t, lng]);

  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="emissions-by-customer-chart">
        <h3 className="emissions-by-customer-chart__title">{t('cpi.analytics.emissionsByCustomer')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="emissions-by-customer-chart emissions-by-customer-chart--loading">
        <h3 className="emissions-by-customer-chart__title">{t('cpi.analytics.emissionsByCustomer')}</h3>
        <div className="emissions-by-customer-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="emissions-by-customer-chart">
        <h3 className="emissions-by-customer-chart__title">{t('cpi.analytics.emissionsByCustomer')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="emissions-by-customer-chart">
      <h3 className="emissions-by-customer-chart__title">{t('cpi.analytics.emissionsByCustomer')}</h3>
      <div ref={chartCallbackRef} style={{ height: '350px', width: '100%' }} />
    </div>
  );
};

export default EmissionsByCustomerChart;
